import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Download, MoreVertical, Search, TrendingUp, Users, Wine, AlertTriangle, CheckCircle, XCircle, Copy, Edit, Trash2, X } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Card } from '../components/SubscriptionAppCard';
import { StatCard } from '../components/StatCard';
import { Input } from '../components/SubscriptionAppInput';
import { Checkbox } from '../components/Checkbox';
import { Divider } from '../components/SubscriptionAppDivider';
import { Pagination } from '../components/SubscriptionAppPagination';
import { TableFilter, Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
// Mock Data
const allocationData = {
  id: '1',
  name: '2023 Reserve Shiraz Release',
  status: 'Active',
  startDate: '2026-03-01',
  endDate: '2026-03-15',
  earlyAccessDate: '2026-02-27',
  earlyAccessTier: 'Vintner',
  totalAvailable: 200,
  totalClaimed: 142,
  revenue: 28400,
  eligibleCount: 485,
  purchasedCount: 142,
  avgOrderValue: 200
};
const productsData = [{
  id: 'p1',
  name: '2023 Reserve Shiraz',
  variant: '750ml',
  sku: 'SHZ-23-RES',
  allocated: 200,
  claimed: 128,
  price: 120
}, {
  id: 'p2',
  name: '2023 Reserve Shiraz',
  variant: 'Magnum',
  sku: 'SHZ-23-RES-MAG',
  allocated: 50,
  claimed: 14,
  price: 300
}];
const claimVelocityData = [{
  date: '1 Mar',
  claims: 12
}, {
  date: '2 Mar',
  claims: 45
}, {
  date: '3 Mar',
  claims: 89
}, {
  date: '4 Mar',
  claims: 112
}, {
  date: '5 Mar',
  claims: 128
}, {
  date: '6 Mar',
  claims: 135
}, {
  date: '7 Mar',
  claims: 142
} // Today
];
const membersData = [{
  id: 'm1',
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  tier: 'Sommelier',
  entitlement: 'Max 6 bottles',
  purchased: 6,
  remaining: 0,
  status: 'Exhausted',
  lastActivity: '2 hours ago'
}, {
  id: 'm2',
  name: 'Michael Chen',
  email: 'michael.c@example.com',
  tier: 'Connoisseur',
  entitlement: 'Max 6 bottles',
  purchased: 0,
  remaining: 6,
  status: 'Invited',
  lastActivity: '-'
}, {
  id: 'm3',
  name: 'Emma Williams',
  email: 'emma.w@example.com',
  tier: 'Vintner',
  entitlement: 'Max 12 bottles',
  purchased: 6,
  remaining: 6,
  status: 'Purchased',
  lastActivity: '1 day ago'
}, {
  id: 'm4',
  name: 'James Anderson',
  email: 'james.a@example.com',
  tier: 'Enthusiast',
  entitlement: 'Max 3 bottles',
  purchased: 3,
  remaining: 0,
  status: 'Exhausted',
  lastActivity: '3 days ago'
}, {
  id: 'm5',
  name: 'Olivia Martinez',
  email: 'olivia.m@example.com',
  tier: 'Sommelier',
  entitlement: 'Max 6 bottles',
  purchased: 2,
  remaining: 4,
  status: 'Purchased',
  lastActivity: '5 hours ago'
}, {
  id: 'm6',
  name: 'William Taylor',
  email: 'w.taylor@example.com',
  tier: 'Connoisseur',
  entitlement: 'Max 6 bottles',
  purchased: 0,
  remaining: 6,
  status: 'Expired',
  lastActivity: '1 week ago'
}, {
  id: 'm7',
  name: 'Sophia Brown',
  email: 'sophia.b@example.com',
  tier: 'Enthusiast',
  entitlement: 'Max 3 bottles',
  purchased: 1,
  remaining: 2,
  status: 'Purchased',
  lastActivity: 'Yesterday'
}, {
  id: 'm8',
  name: 'Liam Wilson',
  email: 'liam.w@example.com',
  tier: 'Vintner',
  entitlement: 'Max 12 bottles',
  purchased: 12,
  remaining: 0,
  status: 'Exhausted',
  lastActivity: '4 days ago'
}];
const ordersData = [{
  id: '#4521',
  member: 'Sarah Johnson',
  products: '6x Reserve Shiraz',
  qty: 6,
  total: 720,
  date: 'Mar 7, 2026'
}, {
  id: '#4520',
  member: 'Emma Williams',
  products: '6x Reserve Shiraz',
  qty: 6,
  total: 720,
  date: 'Mar 6, 2026'
}, {
  id: '#4519',
  member: 'James Anderson',
  products: '3x Reserve Shiraz',
  qty: 3,
  total: 360,
  date: 'Mar 4, 2026'
}, {
  id: '#4518',
  member: 'Olivia Martinez',
  products: '2x Reserve Shiraz',
  qty: 2,
  total: 240,
  date: 'Mar 7, 2026'
}, {
  id: '#4517',
  member: 'Sophia Brown',
  products: '1x Reserve Shiraz',
  qty: 1,
  total: 120,
  date: 'Mar 6, 2026'
}];
export function AllocationDetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [duplicateName, setDuplicateName] = useState(`${allocationData.name} (Copy)`);
  const [notifyOnClose, setNotifyOnClose] = useState(true);
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    status: [],
    tier: []
  });
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  // Filters Configuration
  const availableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: 'invited',
      label: 'Invited',
      value: 'Invited'
    }, {
      id: 'purchased',
      label: 'Purchased',
      value: 'Purchased'
    }, {
      id: 'exhausted',
      label: 'Exhausted',
      value: 'Exhausted'
    }, {
      id: 'expired',
      label: 'Expired',
      value: 'Expired'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'tier',
    label: 'Tier',
    options: [{
      id: 'vintner',
      label: 'Vintner',
      value: 'Vintner'
    }, {
      id: 'sommelier',
      label: 'Sommelier',
      value: 'Sommelier'
    }, {
      id: 'connoisseur',
      label: 'Connoisseur',
      value: 'Connoisseur'
    }, {
      id: 'enthusiast',
      label: 'Enthusiast',
      value: 'Enthusiast'
    }],
    selectedValues: filterValues.tier || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('tier')
  }];
  // Handlers
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.delete(filterId);
    setAddedFilters(newAdded);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpen = new Set(openFilterDropdowns);
    if (newOpen.has(filterId)) {
      newOpen.delete(filterId);
    } else {
      newOpen.add(filterId);
    }
    setOpenFilterDropdowns(newOpen);
  };
  const handleAddFilter = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.add(filterId);
    setAddedFilters(newAdded);
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Purchased':
        return 'success';
      case 'Exhausted':
        return 'info';
      case 'Expired':
        return 'error';
      default:
        return 'default';
    }
  };
  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Vintner':
        return 'bg-[#FEF3C7] text-[#D97706] border-[#FDE68A]';
      case 'Sommelier':
        return 'bg-[#EDE9FE] text-[#7C3AED] border-[#DDD6FE]';
      case 'Connoisseur':
        return 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE]';
      default:
        return 'bg-[#D1FAE5] text-[#059669] border-[#A7F3D0]';
    }
  };
  const getProgressColor = (claimed: number, total: number) => {
    const percentage = claimed / total * 100;
    if (percentage >= 90) return 'bg-[#EF4444]';
    if (percentage >= 75) return 'bg-[#F59E0B]';
    return 'bg-[#10B981]';
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-4035">
      {/* NavBar */}
      <div className="mb-6" data-id="element-4036">
        <NavBar tabs={[]} title="Allocation Details" data-id="element-4037" />
      </div>

      {/* Header */}
      <div className="flex flex-col gap-6 mb-8" data-id="element-4038">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-4039">
          <div className="flex items-center gap-4" data-id="element-4040">
            <button onClick={() => navigate('/allocations')} className="p-2 hover:bg-white rounded-full transition-colors text-[#64748B] hover:text-[#0F172A]" data-id="element-4041">
              <ArrowLeft className="w-5 h-5" data-id="element-4042" />
            </button>
            <div data-id="element-4043">
              <div className="flex items-center gap-3" data-id="element-4044">
                <h1 className="text-2xl font-bold text-[#0F172A]" data-id="element-4045">
                  {allocationData.name}
                </h1>
                <Badge variant="success" data-id="element-4046">Active</Badge>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-4047">
            <Button variant="primary" iconLeft={<Edit className="w-[18px] h-[18px]" data-id="element-4049" />} onClick={() => navigate(`/allocations/${id}/edit`)} data-id="element-4048">
              Edit
            </Button>
            <Button variant="primary" iconLeft={<Copy className="w-[18px] h-[18px]" data-id="element-4051" />} onClick={() => {
            setDuplicateName(`${allocationData.name} (Copy)`);
            setShowDuplicateModal(true);
          }} data-id="element-4050">
              Duplicate
            </Button>
            <Button variant="danger" iconLeft={<XCircle className="w-[18px] h-[18px]" data-id="element-4053" />} onClick={() => setShowCloseModal(true)} data-id="element-4052">
              Close Allocation
            </Button>
          </div>
        </div>

        {/* Info Bar */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-4054">
          <div className="flex items-center gap-6" data-id="element-4055">
            <div className="flex items-center gap-2 text-[#0F172A]" data-id="element-4056">
              <Calendar className="w-5 h-5 text-[#64748B]" data-id="element-4057" />
              <span className="font-medium" data-id="element-4058">1 March 2026 — 15 March 2026</span>
            </div>
            <div className="flex items-center gap-2 text-[#0EA5E9] bg-[#E0F2FE] px-3 py-1 rounded-full text-sm font-medium" data-id="element-4059">
              <Clock className="w-4 h-4" data-id="element-4060" />8 days remaining
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-[#64748B]" data-id="element-4061">
            <span className="font-medium text-[#0F172A]" data-id="element-4062">Early access:</span>
            <span data-id="element-4063">27 Feb</span>
            <span className="bg-[#FEF3C7] text-[#D97706] px-2 py-0.5 rounded text-xs font-medium border border-[#FDE68A]" data-id="element-4064">
              Vintner tier
            </span>
          </div>
        </div>
      </div>

      {/* ROW 1: KPI Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-id="element-4065">
        <StatCard label="Total Claimed" value={`${allocationData.totalClaimed} / ${allocationData.totalAvailable}`} trend={{
        value: '71%',
        isPositive: true
      }} data-id="element-4066" />
        <StatCard label="Revenue" value={`$${allocationData.revenue.toLocaleString()}`} data-id="element-4067" />
        <StatCard label="Eligible Members" value={allocationData.eligibleCount} data-id="element-4068" />
        <StatCard label="Purchased" value={`${allocationData.purchasedCount} members`} trend={{
        value: '29% conv.',
        isPositive: true
      }} data-id="element-4069" />
        <StatCard label="Avg. Order Value" value={`$${allocationData.avgOrderValue}`} data-id="element-4070" />
      </div>

      {/* ROW 2: Products & Velocity */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8" data-id="element-4071">
        {/* Products Table */}
        <div className="lg:col-span-3" data-id="element-4072">
          <Card padding="none" className="h-full flex flex-col" data-id="element-4073">
            <div className="p-6 border-b border-[#E2E8F0]" data-id="element-4074">
              <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-4075">Products</h2>
            </div>
            <div className="flex-1 overflow-x-auto" data-id="element-4076">
              <table className="w-full" data-id="element-4077">
                <thead className="bg-[#F8FAFC]" data-id="element-4078">
                  <tr data-id="element-4079">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4080">
                      Product
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4081">
                      Allocated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-4082">
                      Claimed
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4083">
                      Remaining
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4084">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4085">
                  {productsData.map(product => {
                  const percentage = Math.round(product.claimed / product.allocated * 100);
                  return <tr key={product.id} className="hover:bg-[#F8FAFC]" data-id="element-4086">
                        <td className="px-6 py-4" data-id="element-4087">
                          <div className="flex items-center gap-3" data-id="element-4088">
                            <div className="w-8 h-8 rounded bg-[#F1F5F9] flex items-center justify-center flex-shrink-0" data-id="element-4089">
                              <Wine className="w-4 h-4 text-[#94A3B8]" data-id="element-4090" />
                            </div>
                            <div data-id="element-4091">
                              <div className="text-sm font-medium text-[#0F172A]" data-id="element-4092">
                                {product.name}
                              </div>
                              <div className="text-xs text-[#64748B]" data-id="element-4093">
                                {product.variant}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-[#64748B]" data-id="element-4094">
                          {product.allocated}
                        </td>
                        <td className="px-6 py-4" data-id="element-4095">
                          <div className="w-full" data-id="element-4096">
                            <div className="flex justify-between text-xs mb-1" data-id="element-4097">
                              <span className="font-medium text-[#0F172A]" data-id="element-4098">
                                {product.claimed}
                              </span>
                              <span className="text-[#64748B]" data-id="element-4099">
                                {percentage}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-4100">
                              <div className={`h-full rounded-full ${getProgressColor(product.claimed, product.allocated)}`} style={{
                            width: `${percentage}%`
                          }} data-id="element-4101" />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-4102">
                          {product.allocated - product.claimed}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-4103">
                          ${(product.claimed * product.price).toLocaleString()}
                        </td>
                      </tr>;
                })}
                </tbody>
                <tfoot className="bg-[#F8FAFC] border-t border-[#E2E8F0]" data-id="element-4104">
                  <tr data-id="element-4105">
                    <td className="px-6 py-3 text-sm font-bold text-[#0F172A]" data-id="element-4106">
                      Totals
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-bold text-[#0F172A]" data-id="element-4107">
                      {productsData.reduce((sum, p) => sum + p.allocated, 0)}
                    </td>
                    <td className="px-6 py-3" data-id="element-4108"></td>
                    <td className="px-6 py-3 text-right text-sm font-bold text-[#0F172A]" data-id="element-4109">
                      {productsData.reduce((sum, p) => sum + (p.allocated - p.claimed), 0)}
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-bold text-[#0F172A]" data-id="element-4110">
                      $
                      {productsData.reduce((sum, p) => sum + p.claimed * p.price, 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        {/* Claim Velocity Chart */}
        <div className="lg:col-span-2" data-id="element-4111">
          <Card className="h-full flex flex-col" data-id="element-4112">
            <div className="flex justify-between items-start mb-6" data-id="element-4113">
              <div data-id="element-4114">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-4115">
                  Claim Velocity
                </h2>
                <p className="text-sm text-[#64748B]" data-id="element-4116">
                  Cumulative units claimed over time
                </p>
              </div>
              <div className="text-right" data-id="element-4117">
                <div className="text-sm font-medium text-[#64748B]" data-id="element-4118">
                  Projected sell-out
                </div>
                <div className="text-sm font-bold text-[#0EA5E9]" data-id="element-4119">12 Mar</div>
              </div>
            </div>

            <div className="flex-1 min-h-[200px]" data-id="element-4120">
              <ResponsiveContainer width="100%" height="100%" data-id="element-4121">
                <AreaChart data={claimVelocityData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-4122">
                  <defs data-id="element-4123">
                    <linearGradient id="colorClaims" x1="0" y1="0" x2="0" y2="1" data-id="element-4124">
                      <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.1} data-id="element-4125" />
                      <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} data-id="element-4126" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4127" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#94A3B8'
                }} dy={10} data-id="element-4128" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#94A3B8'
                }} data-id="element-4129" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-4130" />
                  <ReferenceLine y={200} stroke="#94A3B8" strokeDasharray="3 3" label={{
                  value: 'Cap (200)',
                  position: 'insideTopRight',
                  fill: '#94A3B8',
                  fontSize: 12
                }} data-id="element-4131" />
                  <Area type="monotone" dataKey="claims" stroke="#0EA5E9" strokeWidth={2} fillOpacity={1} fill="url(#colorClaims)" data-id="element-4132" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>

      {/* ROW 3: Members Table */}
      <div className="mb-8" data-id="element-4133">
        <Card padding="none" data-id="element-4134">
          <div className="p-6 border-b border-[#E2E8F0]" data-id="element-4135">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-4136">
              <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-4137">Members</h2>

              <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-4138">
                <div className="w-full sm:w-64" data-id="element-4139">
                  <Input placeholder="Search members..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-4141" />} data-id="element-4140" />
                </div>
                <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-4142" />
              </div>
            </div>

            {addedFilters.size > 0 && <div className="mt-4" data-id="element-4143">
                <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-4144" />
              </div>}
          </div>

          <div className="overflow-x-auto" data-id="element-4145">
            <table className="w-full" data-id="element-4146">
              <thead className="bg-[#F8FAFC]" data-id="element-4147">
                <tr data-id="element-4148">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4149">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4150">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4151">
                    Entitlement
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4152">
                    Purchased
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4153">
                    Remaining
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4154">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4155">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 w-10" data-id="element-4156"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4157">
                {membersData.map(member => <tr key={member.id} className="hover:bg-[#F8FAFC]" data-id="element-4158">
                    <td className="px-6 py-4" data-id="element-4159">
                      <div data-id="element-4160">
                        <div className="text-sm font-bold text-[#0F172A]" data-id="element-4161">
                          {member.name}
                        </div>
                        <div className="text-xs text-[#64748B]" data-id="element-4162">
                          {member.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4163">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getTierColor(member.tier)}`} data-id="element-4164">
                        {member.tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#475569]" data-id="element-4165">
                      {member.entitlement}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-4166">
                      {member.purchased}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-[#64748B]" data-id="element-4167">
                      {member.remaining}
                    </td>
                    <td className="px-6 py-4" data-id="element-4168">
                      <Badge variant={getStatusBadgeVariant(member.status)} data-id="element-4169">
                        {member.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-4170">
                      {member.lastActivity}
                    </td>
                    <td className="px-6 py-4" data-id="element-4171">
                      <button className="text-[#94A3B8] hover:text-[#0F172A]" data-id="element-4172">
                        <MoreVertical className="w-4 h-4" data-id="element-4173" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-[#E2E8F0]" data-id="element-4174">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-4175">
              <div className="text-sm text-[#64748B]" data-id="element-4176">
                Showing 1–8 of 485 members
              </div>
              <div className="flex gap-2" data-id="element-4177">
                <Button variant="outline" size="sm" disabled data-id="element-4178">
                  Previous
                </Button>
                <Button variant="outline" size="sm" data-id="element-4179">
                  Next
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* ROW 4: Orders Table */}
      <div className="mb-8" data-id="element-4180">
        <Card padding="none" data-id="element-4181">
          <div className="p-6 border-b border-[#E2E8F0]" data-id="element-4182">
            <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-4183">
              Recent Orders
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-4184">
            <table className="w-full" data-id="element-4185">
              <thead className="bg-[#F8FAFC]" data-id="element-4186">
                <tr data-id="element-4187">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4188">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4189">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4190">
                    Products
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4191">
                    Qty
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4192">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4193">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4194">
                {ordersData.map(order => <tr key={order.id} className="hover:bg-[#F8FAFC]" data-id="element-4195">
                    <td className="px-6 py-4" data-id="element-4196">
                      <a href="#" className="text-sm font-medium text-[#0EA5E9] hover:underline" data-id="element-4197">
                        {order.id}
                      </a>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#0F172A]" data-id="element-4198">
                      {order.member}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-4199">
                      {order.products}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-[#0F172A]" data-id="element-4200">
                      {order.qty}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-4201">
                      ${order.total}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-4202">
                      {order.date}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-[#E2E8F0] text-center" data-id="element-4203">
            <button className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] hover:underline" data-id="element-4204">
              View All Orders →
            </button>
          </div>
        </Card>
      </div>

      {/* Duplicate Allocation Modal */}
      {showDuplicateModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-4205">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-4206">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-4207">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-4208">
                Duplicate Allocation
              </h3>
            </div>
            <div className="p-6 space-y-4" data-id="element-4209">
              <div data-id="element-4210">
                <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-4211">
                  Allocation Name
                </label>
                <Input value={duplicateName} onChange={e => setDuplicateName(e.target.value)} data-id="element-4212" />
                <p className="text-xs text-[#64748B] mt-2" data-id="element-4213">
                  This creates a new draft allocation with the same products,
                  eligibility rules, and notification settings.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex justify-end gap-3" data-id="element-4214">
              <Button variant="secondary" onClick={() => setShowDuplicateModal(false)} data-id="element-4215">
                Cancel
              </Button>
              <Button variant="primary" onClick={() => {
            setShowDuplicateModal(false);
            navigate('/allocations/create');
          }} data-id="element-4216">
                Create Duplicate
              </Button>
            </div>
          </div>
        </div>}

      {/* Close Allocation Modal */}
      {showCloseModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-4217">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-4218">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-4219">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-4220">
                Close Allocation
              </h3>
            </div>
            <div className="p-6 space-y-5" data-id="element-4221">
              <div className="flex items-start gap-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4" data-id="element-4222">
                <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" data-id="element-4223" />
                <p className="text-sm text-[#991B1B]" data-id="element-4224">
                  This will immediately close the allocation and prevent any
                  further purchases.{' '}
                  <span className="font-semibold" data-id="element-4225">
                    {allocationData.eligibleCount - allocationData.purchasedCount}{' '}
                    members
                  </span>{' '}
                  still have unclaimed entitlements.
                </p>
              </div>

              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 space-y-2" data-id="element-4226">
                <div className="flex justify-between text-sm" data-id="element-4227">
                  <span className="text-[#64748B]" data-id="element-4228">Units claimed</span>
                  <span className="font-medium text-[#0F172A]" data-id="element-4229">
                    {allocationData.totalClaimed} of{' '}
                    {allocationData.totalAvailable}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-4230">
                  <span className="text-[#64748B]" data-id="element-4231">
                    Units released back to inventory
                  </span>
                  <span className="font-medium text-[#0F172A]" data-id="element-4232">
                    {allocationData.totalAvailable - allocationData.totalClaimed}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-4233">
                <div className="flex items-center h-5 mt-0.5" data-id="element-4234">
                  <input id="notifyOnClose" type="checkbox" checked={notifyOnClose} onChange={e => setNotifyOnClose(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-4235" />
                </div>
                <label htmlFor="notifyOnClose" className="text-sm text-[#0F172A]" data-id="element-4236">
                  Notify eligible members that this allocation has closed
                </label>
              </div>
            </div>
            <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex justify-end gap-3" data-id="element-4237">
              <Button variant="secondary" onClick={() => setShowCloseModal(false)} data-id="element-4238">
                Cancel
              </Button>
              <Button variant="danger" onClick={() => {
            setShowCloseModal(false);
            navigate('/allocations');
          }} data-id="element-4239">
                Close Allocation
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}